import { MaterialIcon } from '@ampcore/adel-react'
import { AmpcoreMaterialIconId } from '@ampcore/ampcom-contracts'
import { AdvisorAttributes } from '@ampcore/client-side-services'
import { sanitizeUrl } from '@ampcore/string-format'
import React from 'react'
import xss from 'xss'

import { rteContentFilter } from '../../lib/helpers/rteContentFilter'
import { SiteNavAnalytics } from '../../lib/helpers/SiteNavAnalytics'

export type DynamicAdvisorPersonalizedProspectProps = {
  advisorAttributes: AdvisorAttributes
  disclosure: string
  testId?: string
}

export const DynamicAdvisorPersonalizedProspect: React.FC<
  DynamicAdvisorPersonalizedProspectProps
> = props => {
  const { advisorAttributes, disclosure, testId } = props
  const analytics = new SiteNavAnalytics()

  return (
    <div
      className="DynamicAdvisor DynamicAdvisor--personalizedView u-bgSizeCover u-bgColorMidnightBlue"
      style={{
        backgroundImage: `url(${sanitizeUrl(
          window.AMPCOMPATH + 'images/facets/hero-facets-dark-midnight-blue.png'
        )})`
      }}
      data-analytics-container="Advisor"
      data-testid={testId}
    >
      <div className="u-sizeConstrained u-paddingVert32">
        <div className="Content--reversed u-textCenter u-marginBottom20">
          <h2 className="u-textSecondary u-textSizeMsrp4 u-marginBottom4">
            Take the first step toward a more confident financial future.
          </h2>
          <p>{`Connect with ${xss(
            advisorAttributes.name ?? ''
          )} for a complimentary initial consultation today.`}</p>
        </div>
        <div className="Card">
          <div className="Grid Card-body u-padding20">
            <div className="u-size8of12 u-flex u-flexRow u-paddingRight20 u-md-sizeFull u-sm-sizeFull u-sm-flexCol u-md-paddingRight0 u-sm-paddingRight0">
              <div className="u-marginRight20 u-sm-marginRight0 u-sm-marginBottom20">
                <img
                  className={'u-maxWidth100'}
                  alt={
                    advisorAttributes.image
                      ? xss(advisorAttributes.name ?? '')
                      : 'Ameriprise Logo'
                  }
                  src={sanitizeUrl(
                    xss(
                      advisorAttributes.image ||
                        `${window.AMPCOMPATH}images/dac-missing-image.png`
                    )
                  )}
                  data-testid={testId ? `${testId}-image` : undefined}
                />
              </div>
              <div>
                <h3
                  className="u-textBold u-marginBottom8 u-textSizeMsrp2"
                  data-testid={testId ? `${testId}-name` : undefined}
                >
                  {xss(advisorAttributes.name ?? '')}
                </h3>
                {advisorAttributes.tagline && (
                  <p
                    dangerouslySetInnerHTML={{
                      __html: rteContentFilter(advisorAttributes.tagline)
                    }}
                  ></p>
                )}
                <div className="u-md-hidden u-sm-hidden">
                  {advisorAttributes.url && (
                    <a
                      className="Button Button--primary u-marginTop20 u-marginBottom8 u-md-sizeFull u-sm-sizeFull"
                      href={sanitizeUrl(
                        xss(advisorAttributes.url) +
                          '?openFormModal=request-contact'
                      )}
                      onClick={() => {
                        analytics.ping(
                          'Request consultation',
                          'Personalized DAC'
                        )
                      }}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={
                        testId ? `${testId}-consultation-link` : undefined
                      }
                    >
                      Request a consultation
                    </a>
                  )}
                  <p>
                    Or,{' '}
                    <a
                      href={sanitizeUrl(window.AMERIPRISE_ADVISORS_SITE_URL)}
                      onClick={() => {
                        analytics.ping(
                          'Search for a new advisor',
                          'Personalized DAC'
                        )
                      }}
                    >
                      keep searching
                    </a>{' '}
                    for a financial advisor.
                  </p>
                </div>
              </div>
            </div>
            <div className="u-lg-hidden u-size12of12">
              {advisorAttributes.url && (
                <a
                  className="Button Button--primary u-marginTop20 u-marginBottom8 u-md-sizeFull u-sm-sizeFull"
                  href={sanitizeUrl(
                    xss(advisorAttributes.url) +
                      '?openFormModal=request-contact'
                  )}
                  onClick={() => {
                    analytics.ping('Request consultation', 'Personalized DAC')
                  }}
                  target="_blank"
                  rel="noreferrer"
                >
                  Request a consultation
                </a>
              )}
              <p>
                Or,{' '}
                <a
                  href={sanitizeUrl(window.AMERIPRISE_ADVISORS_SITE_URL)}
                  onClick={() => {
                    analytics.ping(
                      'Search for a new advisor',
                      'Personalized DAC'
                    )
                  }}
                >
                  keep searching
                </a>{' '}
                for a financial advisor.
              </p>
            </div>
            <div className="u-size4of12 u-paddingLeft20 u-borderLeft u-md-sizeFull u-md-paddingLeft0 u-md-paddingTop20 u-md-borderLeft0 u-md-marginTop20 u-md-borderTop u-sm-sizeFull u-sm-paddingLeft0 u-sm-paddingTop16 u-sm-marginTop20 u-sm-borderLeft0">
              <div className="u-textPrimary u-textColorNeutralGrey u-textBold u-marginBottom8 u-sm-hidden">
                Contact
              </div>
              <ul className="DynamicAdvisor-contactList Grid u-sm-hidden">
                {advisorAttributes.email && (
                  <li className="List-item u-padding0 u-sizeFull u-flex u-flexAlignItemsCenter u-marginBottom4 u-md-size6of12 u-sm-marginBottom8">
                    <MaterialIcon
                      icon={AmpcoreMaterialIconId.MAIL}
                      ariaLabel="email"
                      className="u-textColorActionBlue u-marginRight8"
                    />
                    <a
                      href={sanitizeUrl(
                        `mailto:${xss(advisorAttributes.email)}`
                      )}
                      onClick={() => {
                        analytics.ping('Email', 'Personalized DAC')
                      }}
                      data-testid={testId ? `${testId}-email-link` : undefined}
                    >
                      {xss(advisorAttributes.email)}
                    </a>
                  </li>
                )}
                {advisorAttributes.phonenumber && (
                  <li className="List-item u-padding0 u-sizeFull u-flex u-flexAlignItemsCenter u-marginBottom4 u-md-size6of12 u-sm-marginBottom8">
                    <MaterialIcon
                      icon={AmpcoreMaterialIconId.PHONE}
                      ariaLabel="phone"
                      className="u-textColorActionBlue u-marginRight8"
                    />
                    <a
                      href={sanitizeUrl(
                        `tel:${xss(advisorAttributes.phonenumber)}`
                      )}
                      onClick={() => {
                        analytics.ping('Call', 'Personalized DAC')
                      }}
                      data-testid={testId ? `${testId}-phone-link` : undefined}
                    >
                      {xss(advisorAttributes.phonenumber)}
                    </a>
                  </li>
                )}
                {advisorAttributes.url && (
                  <li className="List-item u-padding0 u-sizeFull u-flex u-flexAlignItemsCenter u-marginBottom4 u-md-size6of12 u-sm-marginBottom8">
                    <MaterialIcon
                      icon={AmpcoreMaterialIconId.NEW_WINDOW}
                      className="u-textColorActionBlue u-marginRight8"
                    />
                    <a
                      href={sanitizeUrl(xss(advisorAttributes.url))}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={
                        testId ? `${testId}-website-link` : undefined
                      }
                    >
                      Advisor website
                    </a>
                  </li>
                )}
              </ul>

              <div className="Accordion u-hidden u-sm-block">
                <details className="Accordion-item u-borderBottom0">
                  <summary className="Accordion-toggle u-paddingLeft32 u-textBold">
                    Contact
                  </summary>
                  <div className="u-bgColorWhite u-padding0 u-borderBottom0 u-lg-">
                    <ul className="DynamicAdvisor-contactList List Grid">
                      {advisorAttributes.email && (
                        <li className="List-item u-sizeFull u-flex u-flexAlignItemsCenter u-marginBottom4 u-md-size6of12 u-sm-marginBottom8">
                          <MaterialIcon
                            icon={AmpcoreMaterialIconId.MAIL}
                            ariaLabel="email"
                            className="u-textColorActionBlue u-marginRight8"
                          />
                          <a
                            href={sanitizeUrl(
                              `mailto:${xss(advisorAttributes.email)}`
                            )}
                            onClick={() => {
                              analytics.ping('Email', 'Personalized DAC')
                            }}
                            data-testid={
                              testId ? `${testId}-email-link` : undefined
                            }
                          >
                            {xss(advisorAttributes.email)}
                          </a>
                        </li>
                      )}
                      {advisorAttributes.phonenumber && (
                        <li className="List-item u-sizeFull u-flex u-flexAlignItemsCenter u-marginBottom4 u-md-size6of12 u-sm-marginBottom8">
                          <MaterialIcon
                            icon={AmpcoreMaterialIconId.PHONE}
                            ariaLabel="phone"
                            className="u-textColorActionBlue u-marginRight8"
                          />
                          <a
                            href={sanitizeUrl(
                              `tel:${xss(advisorAttributes.phonenumber)}`
                            )}
                            onClick={() => {
                              analytics.ping('Call', 'Personalized DAC')
                            }}
                            data-testid={
                              testId ? `${testId}-phone-link` : undefined
                            }
                          >
                            {xss(advisorAttributes.phonenumber)}
                          </a>
                        </li>
                      )}
                      {advisorAttributes.url && (
                        <li className="List-item u-sizeFull u-flex u-flexAlignItemsCenter u-marginBottom4 u-md-size6of12 u-sm-marginBottom8">
                          <MaterialIcon
                            icon={AmpcoreMaterialIconId.NEW_WINDOW}
                            ariaLabel="new_window"
                            className="u-textColorActionBlue u-marginRight8"
                          />
                          <a
                            href={sanitizeUrl(xss(advisorAttributes.url))}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={
                              testId ? `${testId}-website-link` : undefined
                            }
                          >
                            Advisor website
                          </a>
                        </li>
                      )}
                    </ul>
                  </div>
                </details>
              </div>
            </div>
          </div>
        </div>
        <div
          className="DynamicAdvisor-disclosure Content Content--reversed u-textSizeMsrm2 u-marginTop8"
          data-testid={testId ? `${testId}-disclosure` : null}
          dangerouslySetInnerHTML={{ __html: rteContentFilter(disclosure) }}
        />
        <div className="DynamicAdvisor-disclosure Content Content--reversed u-textSizeMsrm2">
          <p>
            {'The initial consultation provides an overview of financial planning concepts. ' +
              'You will not receive written analysis and/or recommendations.'}
          </p>
        </div>
      </div>
    </div>
  )
}
